import { PARTNER_RULE_UTM_KEYS, PARTNER_RULE_UTM_MAX_LEN, type PartnerLandingBrandingRuleUtm } from './partnerLandingBranding.types';

const MARKETING_STORAGE_KEY = 'gozig_marketing_context_v1';

const MAX_BODY_BYTES = 2048;

/** Query param a share link carries next to its `utm_*` values (not part of the UTM allowlist). */
export const SHARE_REF_PARAM = 'share_ref';
const SHARE_REF_RE = /^[0-9A-Za-z]{12}$/;

/** The ref if it has the shape of one. */
export function normalizeShareRef(value: string | null | undefined): string | undefined {
    const trimmed = value?.trim();
    return trimmed && SHARE_REF_RE.test(trimmed) ? trimmed : undefined;
}

type StoredMarketingContext = {
    schemaVersion: 1;
    utm: PartnerLandingBrandingRuleUtm;
    /** Share link ref from the landing URL; kept with the UTMs it arrived with. */
    shareRef?: string;
};

function emptyStored(): StoredMarketingContext {
    return { schemaVersion: 1, utm: {} };
}

function readStored(): StoredMarketingContext {
    if (typeof window === 'undefined') {
        return emptyStored();
    }
    try {
        const raw = window.sessionStorage.getItem(MARKETING_STORAGE_KEY);
        if (!raw) {
            return emptyStored();
        }
        const parsed: unknown = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return emptyStored();
        }
        const rec = parsed as Record<string, unknown>;
        if (rec.schemaVersion !== 1) {
            return emptyStored();
        }
        const utmRaw = rec.utm;
        if (!utmRaw || typeof utmRaw !== 'object' || Array.isArray(utmRaw)) {
            return emptyStored();
        }
        const utm: PartnerLandingBrandingRuleUtm = {};
        for (const key of PARTNER_RULE_UTM_KEYS) {
            const v = (utmRaw as Record<string, unknown>)[key];
            if (typeof v !== 'string') {
                continue;
            }
            const trimmed = v.trim();
            if (trimmed.length === 0) {
                continue;
            }
            const max = PARTNER_RULE_UTM_MAX_LEN[key];
            utm[key] = trimmed.length > max ? trimmed.slice(0, max) : trimmed;
        }
        const shareRef = typeof rec.shareRef === 'string' ? normalizeShareRef(rec.shareRef) : undefined;
        return shareRef ? { schemaVersion: 1, utm, shareRef } : { schemaVersion: 1, utm };
    }
    catch {
        return emptyStored();
    }
}

function writeStored(value: StoredMarketingContext): void {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.sessionStorage.setItem(MARKETING_STORAGE_KEY, JSON.stringify(value));
    }
    catch {
    /* sessionStorage may be unavailable */
    }
}

/**
 * When the URL contains at least one allowlisted `utm_*` / `utm_external_source` value, sessionStorage
 * is synced to match the address bar: present params overwrite, omitted params are removed so stale
 * fields (e.g. an old `utm_term`) do not linger after navigating to a shorter campaign URL.
 * On the app root (`pathname` `'/'`) with no such params, `gozig_marketing_context_v1` is removed so a
 * plain visit to `/` does not reuse UTMs from an earlier tab session.
 * On other paths with no such params, existing session values are left unchanged (e.g. after in-app navigation).
 */
export function mergeMarketingParamsFromSearch(search: string, pathname?: string): void {
    if (typeof window === 'undefined') {
        return;
    }
    const normalized = typeof search === 'string' ? search : '';
    const q = normalized.startsWith('?') ? normalized.slice(1) : normalized;
    const params = new URLSearchParams(q);

    const hasAnyUtmInUrl = PARTNER_RULE_UTM_KEYS.some((key) => {
        const raw = params.get(key)?.trim();
        return typeof raw === 'string' && raw.length > 0;
    });

    const onAppRoot = pathname === '/';
    if (onAppRoot && !hasAnyUtmInUrl) {
        try {
            window.sessionStorage.removeItem(MARKETING_STORAGE_KEY);
        }
        catch {
            /* sessionStorage may be unavailable */
        }
        return;
    }

    if (!hasAnyUtmInUrl) {
        return;
    }

    const stored = readStored();
    let changed = false;
    for (const key of PARTNER_RULE_UTM_KEYS) {
        const raw = params.get(key)?.trim();
        if (raw && raw.length > 0) {
            const max = PARTNER_RULE_UTM_MAX_LEN[key];
            const next = raw.length > max ? raw.slice(0, max) : raw;
            if (stored.utm[key] !== next) {
                stored.utm[key] = next;
                changed = true;
            }
        }
        else if (stored.utm[key] !== undefined) {
            delete stored.utm[key];
            changed = true;
        }
    }
    // The ref belongs to the UTM set it arrived with, so it is synced the same way: a new campaign URL
    // without one clears it.
    const shareRef = normalizeShareRef(params.get(SHARE_REF_PARAM));
    if (shareRef !== stored.shareRef) {
        if (shareRef) {
            stored.shareRef = shareRef;
        }
        else {
            delete stored.shareRef;
        }
        changed = true;
    }
    if (!changed) {
        return;
    }
    const json = JSON.stringify(stored);
    if (new TextEncoder().encode(json).length > MAX_BODY_BYTES) {
        return;
    }
    writeStored(stored);
}

/** Payload to append as `utm_*` query params on GET /api/login and GET /api/get-started; only canonical keys, omit if empty. */
export function getMarketingPayloadForLoginPost(): PartnerLandingBrandingRuleUtm | undefined {
    if (typeof window !== 'undefined') {
        mergeMarketingParamsFromSearch(window.location.search, window.location.pathname);
    }
    const stored = readStored();
    const out: PartnerLandingBrandingRuleUtm = {};
    for (const key of PARTNER_RULE_UTM_KEYS) {
        const v = stored.utm[key];
        if (typeof v === 'string' && v.length > 0) {
            out[key] = v;
        }
    }
    return Object.keys(out).length > 0 ? out : undefined;
}

/** Share ref to append as `share_ref` on GET /api/login and GET /api/get-started, alongside the UTMs. */
export function getShareRefForLogin(): string | undefined {
    if (typeof window !== 'undefined') {
        mergeMarketingParamsFromSearch(window.location.search, window.location.pathname);
    }
    const stored = readStored();
    return Object.keys(stored.utm).length > 0 ? stored.shareRef : undefined;
}
